import Invoice from 'app/Private/Invoices/Invoice';
import classNames from 'classnames/bind';
import dayjs from 'dayjs';
import { i18nKeys, useTranslation } from 'locales';
import Amount from 'shared/components/Amount';
import BankAccount from 'shared/components/BankAccount';
import { IconColor, IconName } from 'shared/components/Icon';
import Tooltip from 'shared/components/Tooltip';
import { DropdownDirection } from 'shared/io/Dropdown/Dropdown';
import { transactionActions } from 'store/transaction/transaction.actions';
import { DialogShowId, DialogShowSize, showDialog } from 'store/view/view.actions';
import type { OnClickFunction } from 'types/html-type';
import type { Invoice as TInvoice } from 'types/store/invoice-state';
import type { PaymentHistory } from 'types/store/transaction-state';

import styleIdentifiers from './historyListItem.module.scss';

const styles = classNames.bind(styleIdentifiers);

type Props = {
  item: PaymentHistory;
  action: OnClickFunction;
};

export default function HistoryListItem({ item, action }: Props) {
  const { t, currentLang } = useTranslation();

  const unignorePayment = (paymentId: any) => () => {
    transactionActions.unignorePayments({
      data: {
        payment_ids: [paymentId],
      },
    });
  };

  const detailModal = (invoice: TInvoice) => {
    showDialog({
      id: DialogShowId.INVOICE,
      size: DialogShowSize.LARGE,
      children: <Invoice id={invoice.id} modal />,
    });
  };

  const constructReference = (invoiceRef: TInvoice) => (
    <b className={styles('reference')} onClick={() => detailModal(invoiceRef)}>
      {invoiceRef.reference}
    </b>
  );

  const constructMatchedInfo = () => {
    const list = item.matched_invoices_attributes;
    if (!list || list.length === 0) return t(i18nKeys.BANK_ACCOUNT.HISTORY.PAYMENT_NO_RECONCILE);

    return (
      <div>
        {t(
          item.matched
            ? i18nKeys.BANK_ACCOUNT.HISTORY.RECONCILE_WITH
            : i18nKeys.BANK_ACCOUNT.HISTORY.PARTIALLY_MATCHED_WITH,
        )}
        {': '}
        {list.map((it, index) => (
          <>
            {index > 0 && index < list.length - 1 && ', '}
            {index > 0 && index === list.length - 1 && ' et '}
            {constructReference(it)}
          </>
        ))}
      </div>
    );
  };

  return (
    <tr className={styles('HistoryListItem')}>
      <td>
        <div onClick={action} className={styles('box', 'checkbox', item.checked && 'checked')} />
      </td>
      <td>{item.transaction_id}</td>
      <td>
        <BankAccount value={item.iban} small />
      </td>
      <td>{item.counterparty_name}</td>
      <td>{dayjs(item.value_date).format('L')}</td>
      <td>
        <Amount
          localizedValue={item.amount}
          value={item.localized_money_object?.amount ?? item.amount}
          suffix={item.currency}
        />
      </td>
      <td>{item.communication}</td>
      <td className={styles('actions')}>
        <div>
          {item.ignored && (
            <Tooltip
              className={styles('unignored-payment')}
              contentClass={styles('tooltip')}
              selectorClass={styles('tooltip-selector')}
              oneLine
              direction={DropdownDirection.LEFT}
              icon={IconName.SPINNER}
              size="16px"
              onClick={unignorePayment(item.id)}
            >
              {t(i18nKeys.BANK_ACCOUNT.HISTORY.UNIGNORED_PAYMENT)}
            </Tooltip>
          )}
          <Tooltip
            contentClass={styles('tooltip')}
            oneLine
            direction={DropdownDirection.LEFT}
            icon={IconName.REPLACE}
            size="16px"
            color={(() => {
              if (item.matched) return IconColor.DARK_GREEN;
              if (item.matched_invoices_attributes?.length > 0) return IconColor.WARN;
              return IconColor.DARK;
            })()}
          >
            {constructMatchedInfo()}
          </Tooltip>
        </div>
      </td>
    </tr>
  );
}
